/**
 * Runtime control for OOC embedded images. Separate from the build-time
 * config.oocImages.enabled (which is the default): this lets the user
 * toggle images on/off live from settings, and re-renders the EXISTING
 * OOC log both ways -- off converts each embedded image back to a plain
 * link, on converts image-URL links back into thumbnails.
 */
import { showLightbox } from "./oocLightbox";

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|avif)$/i;

let enabled = true;

export function oocImagesEnabled(): boolean {
  return enabled;
}

export function initOocImagesEnabled(value: boolean): void {
  enabled = value;
}

function isHttpsImage(raw: string): URL | null {
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== "https:") return null;
  if (!IMAGE_EXT.test(u.pathname)) return null;
  return u;
}

function makeThumb(href: string): HTMLImageElement {
  const img = document.createElement("img");
  img.className = "ooc_image";
  img.src = href;
  img.loading = "lazy";
  img.alt = "embedded image";
  img.title = "Click to expand";
  img.addEventListener("click", () => showLightbox(href));
  return img;
}

function makeLink(href: string): HTMLAnchorElement {
  const a = document.createElement("a");
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.textContent = href;
  a.dataset.oocImageLink = "1";
  return a;
}

/**
 * Toggles images and rewrites the existing OOC log accordingly.
 */
export function setOocImagesEnabled(value: boolean): void {
  enabled = value;
  const log = document.getElementById("client_ooclog");
  if (!log) return;

  if (!enabled) {
    // Replace each thumbnail with a link to its source.
    const imgs = Array.from(log.querySelectorAll("img.ooc_image"));
    for (const node of imgs) {
      const img = node as HTMLImageElement;
      const link = makeLink(img.src);
      img.replaceWith(link);
    }
  } else {
    // Convert image-URL links (only those we created) back into thumbnails.
    const links = Array.from(
      log.querySelectorAll("a[data-ooc-image-link]"),
    ) as HTMLAnchorElement[];
    for (const link of links) {
      if (isHttpsImage(link.href)) {
        link.replaceWith(makeThumb(link.href));
      }
    }
  }
}
